import { useMemo, useState } from "react";
import Papa from "papaparse";
import type { ParseResult } from "papaparse";
import { supabase } from "../lib/supabase";
import { useCampaignId } from "../hooks/useCampaignId";

function chunk<T>(arr: T[], size = 1000): T[][] {
  return Array.from({ length: Math.ceil(arr.length / size) }, (_: any, i) =>
    arr.slice(i * size, i * size + size)
  );
}

const trim = (s?: string | null) => (s ?? "").toString().trim();

function extractVuid(row: Record<string, any>): string | null {
  const keys = ["VUID", "Voter ID", "VoterID", "vuid", "voter_id", "voterid"];
  for (const k of keys) {
    if (row[k] != null) {
      const v = trim(row[k]);
      if (v) return v;
    }
  }
  return null;
}

export default function ImportHistory() {
  const campaignId = useCampaignId();

  const [electionName, setElectionName] = useState<string>("");
  const [electionDate, setElectionDate] = useState<string>("");
  const [method, setMethod] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);

  const [parsing, setParsing] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showHow, setShowHow] = useState(true);

  const [stats, setStats] = useState<{ total: number; matched: number; inserted: number; skipped_existing: number; not_found: number }>({
    total: 0, matched: 0, inserted: 0, skipped_existing: 0, not_found: 0
  });

  const canImport = useMemo(
    () => !!(campaignId && file && trim(electionName)),
    [campaignId, file, electionName]
  );

  async function doImport() {
    setErr(null); setMsg(null);
    setStats({ total: 0, matched: 0, inserted: 0, skipped_existing: 0, not_found: 0 });

    if (!campaignId || !file) {
      setErr("Choose a campaign, enter the election name, and select a CSV/XLSX file.");
      return;
    }

    const { data: session } = await supabase.auth.getSession();
    const uid = session?.session?.user?.id ?? null;

    const { data: imp, error: impErr } = await supabase
      .from("campaign_imports")
      .insert([{
        campaign_id: campaignId,
        kind: "history",
        source: file.name || "upload",
        created_by: uid,
        rows_loaded: 0,
      }])
      .select("id")
      .single();

    if (impErr) { setErr("Failed to create import record: " + impErr.message); return; }
    const importId = imp!.id as string;

    setParsing(true);
    let vuidsRaw: string[] = [];

    try {
      if (file.name.toLowerCase().endsWith(".csv")) {
        vuidsRaw = await new Promise<string[]>((resolve, reject) => {
          const acc: string[] = [];
          Papa.parse<Record<string, any>>(file, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (h: string) => h.trim(),
            chunk: (results: ParseResult<Record<string, any>>) => {
              for (const r of results.data) {
                const vuid = extractVuid(r);
                if (vuid) acc.push(vuid);
              }
            },
            complete: () => resolve(acc),
            error: (e: unknown) => reject(e),
          });
        });
      } else {
        throw new Error("XLSX detected. For history on this screen, export to CSV; or use the county/profile importer.");
      }
    } catch (e: any) {
      setParsing(false);
      setErr(e?.message ?? String(e));
      return;
    }

    const vuids = Array.from(new Set(vuidsRaw));
    if (vuids.length === 0) {
      setParsing(false);
      setErr("No VUIDs found. Make sure the file has a 'VUID' (or 'Voter ID') column.");
      return;
    }

    const vuidToCvId = new Map<string, string>();
    for (const part of chunk(vuids, 1000)) {
      const { data, error } = await supabase
        .from("campaign_voters")
        .select("id, voter_id, source_voter_id")
        .eq("campaign_id", campaignId)
        .in("voter_id", part);
      if (error) { setParsing(false); setErr("Lookup error: " + error.message); return; }
      (data ?? []).forEach((row: any) => {
        const key = row.voter_id || row.source_voter_id;
        if (key) vuidToCvId.set(String(key), row.id);
      });
    }
    const matchedIds = Array.from(vuidToCvId.values());
    const notFound = vuids.length - matchedIds.length;

    const existing = new Set<string>();
    for (const part of chunk(matchedIds, 1000)) {
      let q = supabase
        .from("campaign_voter_history")
        .select("voter_id", { count: "exact", head: false })
        .eq("campaign_id", campaignId)
        .eq("election_name", trim(electionName));
      if (trim(electionDate)) q = q.eq("election_date", trim(electionDate));
      const { data, error } = await q.in("voter_id", part);
      if (error) { setParsing(false); setErr("Existing check error: " + error.message); return; }
      (data ?? []).forEach((r: any) => existing.add(r.voter_id));
    }

    const rowsToInsert = matchedIds
      .filter(id => !existing.has(id))
      .map(id => ({
        campaign_id: campaignId!,
        voter_id: id,
        election_name: trim(electionName),
        election_date: trim(electionDate) || null,
        method: trim(method) || null,
        source_import_id: importId
      }));

    let inserted = 0;
    for (const part of chunk(rowsToInsert, 1000)) {
      const { error } = await supabase.from("campaign_voter_history").insert(part);
      if (error) { setParsing(false); setErr("Insert error: " + error.message); return; }
      inserted += part.length;
    }

    await supabase.from("campaign_imports").update({ rows_loaded: inserted }).eq("id", importId);

    setStats({
      total: vuids.length,
      matched: matchedIds.length,
      inserted,
      skipped_existing: matchedIds.length - inserted,
      not_found: notFound
    });

    setParsing(false);
    setMsg(`Imported ${inserted} voter history records for "${trim(electionName)}"${electionDate ? ` (${electionDate})` : ""}.`);
  }

  return (
    <div style={{ maxWidth: 880, margin: "0 auto", padding: 16 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Import Voter History</h1>

      <div style={{ background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 10, padding: 12, marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", cursor: "pointer" }} onClick={() => setShowHow(s => !s)}>
          <div style={{ fontWeight: 600 }}>How this import works</div>
          <div style={{ fontSize: 12, color: "#555" }}>{showHow ? "Hide" : "Show"}</div>
        </div>
        {showHow && (
          <div style={{ marginTop: 8, color: "#374151", lineHeight: 1.5 }}>
            <ol style={{ marginLeft: 18 }}>
              <li><b>Choose a campaign</b> from the top bar.</li>
              <li>Upload a <b>CSV</b> that includes a <b>VUID</b> column.</li>
              <li>Enter an <b>Election Name</b> and optional <b>Election Date</b>.</li>
              <li>Duplicates for the same election are skipped automatically.</li>
            </ol>
          </div>
        )}
      </div>

      {err && (
        <div style={{ background: "#ffe8e8", border: "1px solid #f2b0b0", padding: 8, borderRadius: 6, margin: "10px 0", color: "#a40000" }}>
          {err}
        </div>
      )}
      {msg && (
        <div style={{ background: "#ecfdf5", border: "1px solid #86efac", padding: 8, borderRadius: 6, margin: "10px 0", color: "#166534" }}>
          {msg}
        </div>
      )}

      <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ width: 180 }}>Election Name</span>
          <input
            placeholder="e.g., 2024 Primary"
            value={electionName}
            onChange={e => setElectionName(e.target.value)}
            style={{ padding: 8, border: "1px solid #ddd", borderRadius: 8, minWidth: 280 }}
          />
        </label>

        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ width: 180 }}>Election Date (optional)</span>
          <input
            type="date"
            value={electionDate}
            onChange={e => setElectionDate(e.target.value)}
            style={{ padding: 8, border: "1px solid #ddd", borderRadius: 8, minWidth: 180 }}
          />
        </label>

        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ width: 180 }}>Method (optional)</span>
          <select
            value={method}
            onChange={e => setMethod(e.target.value)}
            style={{ padding: 8, border: "1px solid #ddd", borderRadius: 8, minWidth: 180 }}
          >
            <option value="">—</option>
            <option value="E">E — Early Voting</option>
            <option value="P">P — Election Day</option>
            <option value="A">A — Mail-in</option>
            <option value="Z">Z — Provisional</option>
          </select>
        </label>

        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ width: 180 }}>File</span>
          <input
            type="file"
            accept=".csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={e => setFile(e.target.files?.[0] ?? null)}
            style={{ padding: 6 }}
          />
        </label>
        <div style={{ color: "#6b7280", fontSize: 13 }}>
          This screen parses <b>CSV on the client</b>. For <b>XLSX</b>, export to CSV or use the County/Profile import.
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <button onClick={doImport} disabled={!canImport || parsing} style={{
          padding: "8px 12px", borderRadius: 8, border: "1px solid #0ea5e9",
          background: canImport && !parsing ? "#0ea5e9" : "#9ccff0", color: "#fff", cursor: canImport && !parsing ? "pointer" : "not-allowed"
        }}>
          {parsing ? "Importing…" : "Import History"}
        </button>

        <div style={{ color: "#555" }}>
          {stats.total > 0 && (
            <>
              <b>Total VUIDs:</b> {stats.total} &nbsp;|&nbsp; <b>Matched:</b> {stats.matched} &nbsp;|&nbsp; <b>Inserted:</b> {stats.inserted} &nbsp;|&nbsp; <b>Existing:</b> {stats.skipped_existing} &nbsp;|&nbsp; <b>Not found:</b> {stats.not_found}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
